import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import { toast } from 'react-toastify';
import AdminLayout from '../components/AdminLayout';
import api from '../services/api';

interface AdminReportItem {
  id: number;
  reporter_id: number;
  reporter_profile_id?: string;
  reporter_name: string;
  reported_user_id: number;
  reported_profile_id?: string;
  reported_name: string;
  reason: string;
  description: string;
  status: string;
  created_at: string;
  reviewed_by?: number;
  review_comments?: string;
}

const AdminReportsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<AdminReportItem[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await api.get('/reports/admin/list', {
        params: {
          page: 1,
          limit: 100,
          report_status: statusFilter,
          search: search.trim() || undefined,
        },
      });
      setItems(response.data.items || []);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [statusFilter]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchReports();
    }, 250);
    return () => clearTimeout(timeoutId);
  }, [search]);

  const getStatusColor = (status: string): 'default' | 'warning' | 'error' | 'success' => {
    if (status === 'PENDING') return 'warning';
    if (status === 'UNDER_REVIEW') return 'default';
    if (status === 'REJECTED') return 'error';
    if (status === 'RESOLVED') return 'success';
    return 'default';
  };

  const updateStatus = async (reportId: number, status: string) => {
    try {
      await api.patch(`/reports/admin/${reportId}/status`, {
        status,
      });
      toast.success(`Report marked as ${status.replace('_', ' ')}`);
      await fetchReports();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update report');
    }
  };

  return (
    <AdminLayout currentPage="reports">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Reports & Safety Queue
        </Typography>
        <Typography variant="body2" color="textSecondary">
          See who reported which profile and take moderation action.
        </Typography>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.8fr 140px' }, gap: 1.25 }}>
            <TextField
              size="small"
              label="Search by Profile ID/Name/Email/Phone"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <TextField select size="small" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="ALL">All</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="UNDER_REVIEW">Under Review</MenuItem>
              <MenuItem value="RESOLVED">Resolved</MenuItem>
              <MenuItem value="REJECTED">Rejected</MenuItem>
            </TextField>
            <Button variant="outlined" onClick={() => setSearch('')}>Clear</Button>
          </Box>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
      ) : items.length === 0 ? (
        <Alert severity="info">No reports found for the current filter.</Alert>
      ) : (
        <Card>
          <CardContent>
            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
              <Table sx={{ width: '100%', minWidth: 900, tableLayout: { xs: 'auto', lg: 'fixed' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Reporter</strong></TableCell>
                    <TableCell><strong>Reported Profile</strong></TableCell>
                    <TableCell><strong>Reason</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="right"><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.reporter_name}</Typography>
                        <Typography variant="caption" color="textSecondary">{item.reporter_profile_id ? item.reporter_profile_id : `User ${item.reporter_id}`}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.reported_name}</Typography>
                        <Typography variant="caption" color="textSecondary">{item.reported_profile_id ? item.reported_profile_id : `User ${item.reported_user_id}`}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={item.reason.replace(/_/g, ' ')} />
                      </TableCell>
                      <TableCell sx={{ wordBreak: 'break-word' }}>{item.description}</TableCell>
                      <TableCell>
                        <Chip size="small" color={getStatusColor(item.status)} label={item.status.replace(/_/g, ' ')} />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                          <Button size="small" variant="outlined" onClick={() => updateStatus(item.id, 'UNDER_REVIEW')}>
                            Review
                          </Button>
                          <Button size="small" variant="contained" color="success" onClick={() => updateStatus(item.id, 'RESOLVED')}>
                            Resolve
                          </Button>
                          <Button size="small" variant="outlined" color="error" onClick={() => updateStatus(item.id, 'REJECTED')}>
                            Reject
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
};

export default AdminReportsPage;
